import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Buffer } from 'buffer'

type UserProps = {
  name?: string
  username?: string
  roles: string[]
  token: string
  orgaoExterno?: number[]
  foto?: string
  federationLink?: string
}

type AuthContextProps = {
  user: UserProps | null
  isLoading: boolean
}

type PayloadAttributes = {
  iss: string
  name: string
  preferred_username?: string
  resource_access: {
    mac?: {
      roles?: string[]
    }
  }
  orgaoExterno?: number[]
  foto?: string
  federationLink?: string
}

/** JWT payload uses base64url; `atob` only accepts standard base64. */
function decodeJwtPayloadSegment(token: string): string {
  const parts = token.split('.')
  if (parts.length < 2 || !parts[1]) {
    throw new Error('Invalid JWT: missing payload segment')
  }
  const base64Url = parts[1]
  const padded = base64Url + '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
  return Buffer.from(base64, 'base64').toString('utf8')
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps)

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserProps>()
  const [isLoading, setLoading] = useState<boolean>(true)

  const { user: userAuth } = useAuth()

  useEffect(() => {
    const decodeToken = async () => {
      try {
        if (userAuth && userAuth.access_token) {
          const payload = JSON.parse(
            decodeJwtPayloadSegment(userAuth.access_token),
          ) as PayloadAttributes

          setUser({
            name: payload.name,
            username: payload.preferred_username,
            token: userAuth.access_token,
            roles: payload.resource_access?.mac?.roles || [],
            foto: payload.foto,
            federationLink: payload.federationLink,
          })
          setLoading(false)
        }
      } catch (error) {
        console.error('Error decoding JWT token:', error)
        setLoading(false)
      }
    }

    decodeToken()
  }, [userAuth])

  return (
    <AuthContext.Provider value={{ user: user, isLoading } as AuthContextProps}>
      <>{children}</>
    </AuthContext.Provider>
  )
}

const useAuthContext = () => {
  const { user, isLoading } = useContext(AuthContext)

  const handleHasPermission = (permission: string): boolean => {
    if (user) {
      return user.roles.includes(permission)
    }
    return false
  }

  const hasOrgaoExterno = (orgaoExterno: number) => {
    return true
  }

  return { user, handleHasPermission, hasOrgaoExterno, isLoading }
}

export { AuthProvider, AuthContext, useAuthContext }
